<script setup>
import BaseDialog from '@/components/widgets/BaseDialog.vue';
import ListingMedia from '@/components/media/ListingMedia.vue';
import { useMediaStore } from '@/stores/media';
import { useSnackBarStore } from '@/stores/snackbar';
import { ref, toRef, watch } from 'vue';
import i18n from '@/plugins/i18n';

const props = defineProps({
  images: {
    type: [Object, Array],
    default: () => [],
  },
  subtitle: {
    type: String,
    required: false,
    default: () => '',
  },
  viewCloseButton: {
    type: Boolean,
    required: false,
    default: true,
  },
  directUpload: {
    type: Boolean,
    required: false,
    default: false,
  },
  allowGallerySelection: {
    type: Boolean,
    required: false,
    default: false,
  },
  type: {
    type: String,
    required: false,
    default: 'image/*',
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  icon: {
    type: Object,
    default: () => ({
      file: '/assets/imgs/pages/placeholder.png',
      width: 20,
      height: 20,
    }),
  },
  style: {
    type: Object,
    default: () => ({
      card: '',
      content: '',
      title: '',
      subtitle: '',
    }),
  },
  title: {
    type: String,
    default: 'Click or drag files to this area to upload Icon',
  },
  keys: {
    type: Number,
    default: 1,
  },
  required: {
    type: Boolean,
    default: false,
  },
  hideSearch: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['syncImages']);

const { t } = i18n.global;

const ListingMediaComp = ref(null);
const media = useMediaStore();
const notification = useSnackBarStore();

const show = ref(false);
const imagesCopy = toRef(props, 'images');

const dragActive = ref(false);
const uploadedFiles = ref([]);

const drop = event => {
  _selectedFiles(event.dataTransfer.files);
};

const selectedFile = event => {
  _selectedFiles(event.target.files);
};

const _selectedFiles = files => {
  let maxSize = window.app_configs.max_upload_size || 1024 * 1024;

  uploadedFiles.value = [];
  for (let i = 0; i < files.length; i++) {
    if (files[i].size < maxSize) {
      let uploadedFile = files[i];
      uploadedFile.url = URL.createObjectURL(files[i]);
      uploadedFiles.value.push(uploadedFile);
    }
  }

  if (!uploadedFiles.value.length && files.length) {
    notification.warn(t('fileSizeToBig', { size: parseInt(maxSize / 1024) }));

    return;
  }

  dragActive.value = true;

  if (props.directUpload) {
    uploadFile(uploadedFiles.value[0]);
  } else {
    emit('syncImages', uploadedFiles.value);
  }
};

const clearDropped = file => {
  if (file.id) {
    uploadedFiles.value = uploadedFiles.value.filter(uploadedFile => uploadedFile.id !== file.id);
  } else {
    uploadedFiles.value = uploadedFiles.value.filter(uploadedFile => uploadedFile.name !== file.name);
  }
  dragActive.value = false;
  emit('syncImages', Object.keys(uploadedFiles.value).length ? uploadedFiles.value : null);
};

function getImageSrc(file) {
  if (props.directUpload) {
    return file?.thumb_url || file?.image?.base_url + '/' + file?.image?.webp_image;
  }

  return file?.url || file?.thumb_url || file?.image?.base_url + '/' + file?.image?.webp_image;
}
function chooseImage() {
  const image = ListingMediaComp.value.getSelectedImage();
  if (image) {
    if (!props.multiple) {
      closeChooseDialog();
    }
    emit('syncImages', [image.image]);
  }
}
function openMediaLibrary() {
  show.value = true;
}
function closeChooseDialog() {
  show.value = false;
}

const uploadFile = async file => {
  let form = new FormData();
  form.set('attachment_group_id', 1);
  form.set('name', file.name);
  form.set('attachment', file);
  await media
    .saveFiles(form)
    .then(response => {
      if (!props.multiple) {
        // uploadedFiles.value = [response.data.data];
      }
      emit('syncImages', [response.data.data.image]);
    })
    .catch(notification.responseError);
};

watch(
  imagesCopy,
  val => {
    uploadedFiles.value = [];
    if (!Array.isArray(val)) {
      val = [val];
    }

    if (val) {
      let images = val
        .map(image => {
          if (image) {
            if (!image.id) {
              return {
                url: image.url ?? image?.thumb_url,
                name: image?.name ?? '',
                size: image?.size ?? '',
              };
            } else {
              return image;
            }
          }
        })
        .filter(image => image);

      if (images.length) {
        uploadedFiles.value = images;
      }
    }
  },
  { immediate: true },
);
</script>

<template>
  <div class="img-uploader-cls">
    <div v-if="!multiple && uploadedFiles.length && uploadedFiles[0]">
      <VRow align="center" justify="center" class="text-end">
        <VCol cols="12">
          <VHover v-slot="{ isHovering, props }">
            <VCard :elevation="isHovering ? 2 : 12" :class="{ 'on-hover': isHovering }" v-bind="props" class="d-flex justify-center align-center">
              <VImg
                width="230"
                style="height: 230px"
                :src="getImageSrc(uploadedFiles[0])"
                :alt="uploadedFiles[0].name ?? ''"
                :title="uploadedFiles[0].name ?? '' + ' ' + uploadedFiles[0].size ?? ''"
                lazy-src="/assets/svg/ic_image.svg">
                <template #placeholder>
                  <ProgressBar :size="40" class-name="ma-0" />
                </template>

                <VBtn
                  icon
                  variant="text"
                  :class="{ 'show-btns': isHovering }"
                  color="rgba(255, 255, 255, 0)"
                  @click.prevent.stop="clearDropped(uploadedFiles[0])">
                  <VIcon :class="{ 'show-btns': isHovering }" color="rgba(255, 255, 255, 0)"> mdi-close-thick </VIcon>
                </VBtn>
              </VImg>
            </VCard>
          </VHover>
        </VCol>
      </VRow>
    </div>
    <div
      v-else
      :class="{ 'bg-light-blue': dragActive }"
      class="d-flex flex-column align-center px-4 py-6 border-4 border-gray-300 border-dashed rounded dark:border-2 overflow-y-auto"
      :style="props.style.card"
      @dragover.prevent="dragActive = true"
      @dragenter.prevent="dragActive = true"
      @dragleave.prevent="dragActive = false"
      @drop.prevent="drop">
      <label :for="'file-' + props.keys" class="d-flex flex-column align-center text-center cursor-pointer" :style="props.style.content">
        <span class="cursor-pointer">
          <VImg :src="props.icon.file" :width="props.icon.width" :height="props.icon.height" class="mx-2" cover />
        </span>
        <span :for="'file-' + props.keys" class="text-black input-secondary-label cursor-pointer mt-2" :style="props.style.title">
          {{ props.title }}
        </span>
        <span
          v-if="props.subtitle"
          :for="'file-' + props.keys"
          class="text-text-secondary-color input-secondary-label cursor-pointer mt-2"
          :style="props.style.subtitle">
          {{ props.subtitle }}
        </span>
        <input
          :id="'file-' + props.keys"
          :key="props.keys"
          type="file"
          :multiple="multiple"
          :name="'file-' + props.keys"
          :accept="type"
          class="hidden"
          :required="props.required"
          @change="selectedFile" />
      </label>
    </div>

    <div v-if="allowGallerySelection && (!uploadedFiles.length || multiple)" class="mt-1 text-center">
      <span class="text-text-secondary-color input-secondary-label"> {{ $t('or') }}</span> <br />
      <VBtn color="default" @click="openMediaLibrary">
        {{ $t('chooseFromLibrary') }}
      </VBtn>
    </div>

    <BaseDialog
      v-if="show"
      max-width="60%"
      title="chooseImage"
      positive-btn-text="chooseSelectedImage"
      @close="closeChooseDialog"
      @save="chooseImage">
      <template #content>
        <ListingMedia ref="ListingMediaComp" :init-data="show" @handle-double-click="chooseImage" />
      </template>
    </BaseDialog>
  </div>
</template>

<style scoped>
.hidden {
  display: none;
  visibility: hidden;
}

.v-card {
  transition: opacity 0.4s ease-in-out;
}

.v-card.on-hover {
  opacity: 0.6;
}

.show-btns {
  color: rgb(255, 0, 0) !important;
}
</style>
